import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import {
  BaseResponse,
  BaseResponseSingle,
  ICustomerBillsResponse,
  ICustomerResponse,
  ICustomerNfcTagResponse,
} from "../../typings";
import {
  ADD_STORE_CREDIT,
  ASSIGN_CUSTOMER_NFC_TAG,
  ADD_CUSTOMER_NFC_TAG,
  DELETE_CUSTOMER_NFC_TAG,
  GET_CUSTOMER_BILLS_BY_ID,
  GET_CUSTOMER_BY_ID,
  GET_CUSTOMER_CART_INFO_BY_ID,
  CREATE_AND_UPDATE_USER_NOTE,
  GET_USER_NOTES_BY_ID,
  DELETE_USER_NOTE,
  GET_STORE_CREDIT_HISTORY,
  DELETE_STORE_CREDIT,
  GET_CUSTOMER_INTERACTIONS_BY_DAY,
  INVOICE_HISTORY,
} from "../../configs";
import { AddStoreCreditRequest } from "../../typings/interfaces/requests/addStoreCreditRequest";
import { CustomerCartInfoResponse } from "../../typings/interfaces/responses/customer-cart-info-response";
import {
  CreateUserNoteRequest,
  UpdateUserNoteRequest,
} from "../../typings/interfaces/requests/user-note-requests";
import { CustomerNoteResponse } from "../../typings/interfaces/responses/customer-note-response";
import { StoreCreditHistoryResponse } from "../../typings/interfaces/responses/store-credit-history-response";
import {
  ICreateCustomerRequest,
  IUpdateCustomerRequest,
} from "../../typings/interfaces/requests/customer-requests";
import { CREATE_CUSTOMER, UPDATE_CUSTOMER } from "../../configs";
import { ICustomerActivitiesByDayResponse } from "../../typings/interfaces/responses/customer-activities-by-day-response";
import {
  BillFilterDateType,
  BillPaymentStatus,
  BillPickupStatus,
} from "../../typings/enum/paymentTypes";
import { InvoiceHistoryResponse } from "../../typings/interfaces/responses/invoice-history-response";
import dayjs from "dayjs";

export const createCustomerAction = createAsyncThunk(
  "customer/create",
  async (customer: ICreateCustomerRequest, { rejectWithValue }) => {
    try {
      const payload: ICreateCustomerRequest = {
        ...customer,
        customerStatusPref: customer.customerStatusPref?.length
          ? customer.customerStatusPref
          : ["ACTIVE"],
      };

      const response = await axios.post<BaseResponse<ICustomerResponse>>(
        CREATE_CUSTOMER,
        payload
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const updateCustomerAction = createAsyncThunk(
  "customer/update",
  async (customer: IUpdateCustomerRequest, { rejectWithValue }) => {
    try {
      const response = await axios.put<BaseResponse<ICustomerResponse>>(
        UPDATE_CUSTOMER,
        customer
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const getCustomerByIdAction = createAsyncThunk(
  "customer/getById",
  async (customerId: string, { rejectWithValue }) => {
    try {
      const response = await axios.get<BaseResponseSingle<ICustomerResponse>>(
        GET_CUSTOMER_BY_ID(customerId)
      );
      return response.data.body;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const deleteStoreCreditAction = createAsyncThunk(
  "customer/delete-store-credit",
  async ({ customerId, noteId }: { customerId: number; noteId: number }) => {
    try {
      const response = await axios.delete<BaseResponse<null>>(
        DELETE_STORE_CREDIT(customerId, noteId)
      );
      return response.data;
    } catch (err) {
      return err;
    }
  }
);

export const getCustomerCartInfoByIdAction = createAsyncThunk(
  "customer/get-cart-info",
  async (customerId: string, { rejectWithValue }) => {
    try {
      const response = await axios.get<
        BaseResponseSingle<CustomerCartInfoResponse>
      >(GET_CUSTOMER_CART_INFO_BY_ID(customerId));
      return response.data.body;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const getCustomerBillHistoryAction = createAsyncThunk(
  "customer/get-bill-history",
  async (
    {
      customerId,
      paymentFilterStatus,
      pickingFilterStatus,
      filterDateType,
      invoiceNumber,
      startDate,
      endDate,
      page,
      size,
    }: {
      customerId: number;
      page: number;
      size: number;
      invoiceNumber?: string;
      paymentFilterStatus?: BillPaymentStatus;
      pickingFilterStatus?: BillPickupStatus;
      filterDateType?: BillFilterDateType;
      startDate?: string;
      endDate?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await axios.get<
        BaseResponseSingle<ICustomerBillsResponse>
      >(
        GET_CUSTOMER_BILLS_BY_ID(
          customerId,
          size,
          page,
          paymentFilterStatus,
          filterDateType,
          startDate ? dayjs(startDate).format("YYYY-MM-DD") : undefined,
          endDate ? dayjs(endDate).format("YYYY-MM-DD") : undefined,
          pickingFilterStatus,
          invoiceNumber
        )
      );
      return response.data.body;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const addStoreCreditAction = createAsyncThunk(
  "customer/add-store-credit",
  async (
    { customerId, paymentType, amount, storeUserId, checkNumber }: AddStoreCreditRequest,
    { rejectWithValue }
  ) => {
    try {
      const response = await axios.post<BaseResponse<AddStoreCreditRequest>>(
        ADD_STORE_CREDIT,
        {
          customerId,
          paymentType,
          amount,
          storeUserId,
          checkNumber,
        }
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const getStoreCreditHistoryAction = createAsyncThunk(
  "customer/get-store-credit-history",
  async (customerId: number, { rejectWithValue }) => {
    try {
      const response = await axios.get<
        BaseResponse<StoreCreditHistoryResponse>
      >(GET_STORE_CREDIT_HISTORY(customerId));
      return response.data;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const addUserNoteAction = createAsyncThunk(
  "customer/create-note",
  async (
    { note, occasions, merchantUserId, customerId }: CreateUserNoteRequest,
    { rejectWithValue }
  ) => {
    try {
      const response = await axios.post<BaseResponse<CreateUserNoteRequest>>(
        CREATE_AND_UPDATE_USER_NOTE,
        {
          note,
          occasions,
          merchantUserId,
          customerId,
        }
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const getUserNotesByIdAction = createAsyncThunk(
  "customer/get-notes",
  async (customerId: string, { rejectWithValue }) => {
    try {
      const response = await axios.get<BaseResponse<CustomerNoteResponse>>(
        GET_USER_NOTES_BY_ID(customerId)
      );
      let sorted = response.data.body?.sort(
        (a: any, b: any) =>
          new Date(b.createdTime).getTime() - new Date(a.createdTime).getTime()
      );
      return sorted;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const updateUserNoteAction = createAsyncThunk(
  "customer/update-noet",
  async (
    { id, note, occasions, merchantUserId, customerId }: UpdateUserNoteRequest,
    { rejectWithValue }
  ) => {
    try {
      const response = await axios.put<BaseResponse<UpdateUserNoteRequest>>(
        CREATE_AND_UPDATE_USER_NOTE,
        {
          id,
          note,
          occasions,
          merchantUserId,
          customerId,
        }
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const deleteUserNoteAction = createAsyncThunk(
  "customer/delete-note",
  async (noteId: string, { rejectWithValue }) => {
    try {
      const response = await axios.delete<BaseResponse<null>>(
        DELETE_USER_NOTE(noteId)
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const assignCustomerNfcTagAction = createAsyncThunk(
  "customer/assign-nfc-tag",
  async (
    { customerId, nfcTagId, assignedBy }: { customerId: number; nfcTagId: string; assignedBy: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await axios.put<BaseResponseSingle<ICustomerResponse>>(
        ASSIGN_CUSTOMER_NFC_TAG(customerId),
        { nfcTagId, assignedBy }
      );
      return response.data.body;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const addCustomerNfcTagAction = createAsyncThunk(
  "customer/add-nfc-tag",
  async (
    { customerId, nfcTagId, assignedBy, label }: { customerId: number; nfcTagId: string; assignedBy: string; label?: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await axios.post<BaseResponseSingle<ICustomerNfcTagResponse>>(
        ADD_CUSTOMER_NFC_TAG(customerId),
        { nfcTagId, assignedBy, label }
      );
      return response.data.body;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const removeCustomerNfcTagAction = createAsyncThunk(
  "customer/remove-nfc-tag",
  async (
    { customerId, nfcTagId }: { customerId: number; nfcTagId: string },
    { rejectWithValue }
  ) => {
    try {
      await axios.delete(DELETE_CUSTOMER_NFC_TAG(customerId, nfcTagId));
      return nfcTagId;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const getInvoiceHistoryAction = createAsyncThunk(
  "customer/get-invoice-history",
  async (billId: number, { rejectWithValue }) => {
    try {
      const response = await axios.get<BaseResponse<InvoiceHistoryResponse[]>>(
        INVOICE_HISTORY(billId)
      );
      return response.data.body;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);
